import React, { useEffect, useState, useContext, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  TextField,
  Toolbar,
  Typography,
  Slider,
  Grid,
  Input,
  Paper,
  Dialog,
  DialogTitle,
  DialogActions,
} from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import fileDownload from "js-file-download";
import TablePaginationActions from "../utils/TablePaginationActions";
import ServicesContext from "../../contexts/servicesContext";
import { useAlert } from "../../hooks/useAlert";

const entMaxPoints = 140;
const aetMaxPoints = 95;

const useStyles = makeStyles((theme) => ({
  seeMore: {
    marginTop: theme.spacing(3),
  },
  toolbar: {
    display: "flex",
    flexDirection: "row",
  },
  title: {
    flex: "1 1 100%",
  },
  paper: {
    padding: theme.spacing(2),
    display: "flex",
    overflow: "auto",
    flexDirection: "column",
  },
}));

function EnrolleeList({ showOnlyConfirmed }) {
  const rowsPerPage = 20;
  const showAlert = useAlert();

  const [currentPage, setCurrentPage] = useState(0);

  const [loading, setLoading] = useState(true);
  const [enrollees, setEnrollees] = useState([]);
  const [enrolleesCount, setEnrolleesCount] = useState(0);

  const [searchQuery, setSearchQuery] = useState("");
  const [filter, setFilter] = useState("any");

  const [hasEnglishCertificate, setHasEnglishCertificate] = useState(false);
  const [fullness, setFullness] = useState(false);

  const [entPointsFrom, setEntPointsFrom] = useState(0);
  const [entPointsTo, setEntPointsTo] = useState(0);

  const [aetPointsFrom, setAetPointsFrom] = useState(0);
  const [aetPointsTo, setAetPointsTo] = useState(0);

  const [openDialog, setOpenDialog] = useState(false);

  const classes = useStyles();
  const services = useContext(ServicesContext);

  useEffect(() => {
    setLoading(true);
    const debouncedSearch = setTimeout(() => {
      services.secretary
        .getEnrolless({
          confirm: showOnlyConfirmed,
          application_year: localStorage.getItem("AdmissionOfYear"),
          ...(searchQuery && { type: filter }),
          ...(entPointsFrom && { EntFrom: entPointsFrom }),
          ...(entPointsTo && { EntTo: entPointsTo }),
          ...(aetPointsFrom && { AETEnglishFrom: aetPointsFrom }),
          ...(aetPointsTo && { AETEnglishTo: aetPointsTo }),
          fullness: fullness,
          englishCertification: hasEnglishCertificate,
          page: currentPage,
          ...(searchQuery && { keyword: searchQuery }),
        })
        .then((response) =>
          setEnrollees(() => {
            const { list, total_number } = response.data;
            setEnrolleesCount(total_number);
            setLoading(false);
            return list;
          })
        );
    }, 1500);

    return () => clearInterval(debouncedSearch);
  }, [
    showOnlyConfirmed,
    filter,
    searchQuery,
    currentPage,
    hasEnglishCertificate,
    entPointsFrom,
    entPointsTo,
    aetPointsFrom,
    aetPointsTo,
    fullness,
    services,
    localStorage.getItem("AdmissionOfYear"),
  ]);

  const handleStudentConverter = () => {
    services.secretary
      .convertToStudent({
        confirm: showOnlyConfirmed,
        ...(entPointsFrom && { EntFrom: entPointsFrom }),
        ...(entPointsTo && { EntTo: entPointsTo }),
        ...(aetPointsFrom && { AETEnglishFrom: aetPointsFrom }),
        ...(aetPointsTo && { AETEnglishTo: aetPointsTo }),
        fullness: fullness,
        englishCertification: hasEnglishCertificate,
        application_year: localStorage.getItem("AdmissionOfYear"),
      })
      .then((response) => {
        if (response.status === 200) {
          alert("Абитуренты были успешно были конвертированы");
        }
      })
      .catch((err) => {
        alert(err.message);
      })
      .finally(() => {
        setOpenDialog(false);
      });
  };

  const handleSendToEpvo = (userId) => {
    services.epvo.sendToEpvo(userId)
      .then(() => showAlert('Успешно отправлен в ЕПВО!', 'success'))
      .catch((err) => showAlert(err.response.data.message, 'error'))
  }

  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handleSearchQueryChange = (event) => {
    setSearchQuery(event.target.value);
    setCurrentPage(0);
  };

  const handleFilterChange = (event) => {
    setFilter(event.target.value);
    setCurrentPage(0);
  };

  // Event handlers for ENT Points From
  const handleEntPointsFromSliderChange = (event, newValue) => {
    setEntPointsFrom(newValue);
    setCurrentPage(0);
  };

  const handleEntPointsFromInputChange = (event) => {
    setEntPointsFrom(
      event.target.value === "" ? "" : Number(event.target.value)
    );
    setCurrentPage(0);
  };

  const handleEntPointsFromBlur = () => {
    if (entPointsFrom < 0) {
      setEntPointsFrom(0);
    } else if (entPointsFrom > entMaxPoints) {
      setEntPointsFrom(entMaxPoints);
    }

    setCurrentPage(0);
  };

  // Event handlers for ENT Points To
  const handleEntPointsToSliderChange = (event, newValue) => {
    setEntPointsTo(newValue);
    setCurrentPage(0);
  };

  const handleEntPointsToInputChange = (event) => {
    setEntPointsTo(event.target.value === "" ? "" : Number(event.target.value));
    setCurrentPage(0);
  };

  const handleEntPointsToBlur = () => {
    if (entPointsTo < 0) {
      setEntPointsTo(0);
    } else if (entPointsTo > entMaxPoints) {
      setEntPointsTo(entMaxPoints);
    }

    setCurrentPage(0);
  };

  // Event handlers for AET Points From
  const handleAetPointsFromSliderChange = (event, newValue) => {
    setAetPointsFrom(newValue);
    setCurrentPage(0);
  };

  const handleAetPointsFromInputChange = (event) => {
    setAetPointsFrom(
      event.target.value === "" ? "" : Number(event.target.value)
    );
    setCurrentPage(0);
  };

  const handleAetPointsFromBlur = () => {
    if (aetPointsFrom < 0) {
      setAetPointsFrom(0);
    } else if (aetPointsFrom > aetMaxPoints) {
      setAetPointsFrom(aetMaxPoints);
    }

    setCurrentPage(0);
  };

  // Event handlers for AET Points To
  const handleAetPointsToSliderChange = (event, newValue) => {
    setAetPointsTo(newValue);
    setCurrentPage(0);
  };

  const handleAetPointsToInputChange = (event) => {
    setAetPointsTo(event.target.value === "" ? "" : Number(event.target.value));
    setCurrentPage(0);
  };

  const handleAetPointsToBlur = () => {
    if (aetPointsTo < 0) {
      setAetPointsTo(0);
    } else if (aetPointsTo > aetMaxPoints) {
      setAetPointsTo(aetMaxPoints);
    }

    setCurrentPage(0);
  };

  const data = useMemo(
    () => ({
      confirm: showOnlyConfirmed,
      type: filter,
      ...(entPointsFrom && { EntFrom: entPointsFrom }),
      ...(entPointsTo && { EntTo: entPointsTo }),
      ...(aetPointsFrom && { AETEnglishFrom: aetPointsFrom }),
      ...(aetPointsTo && { AETEnglishTo: aetPointsTo }),
      fullness: fullness,
      englishCertification: hasEnglishCertificate,
      page: currentPage,
      ...(!searchQuery && { type: "any" }),
      ...(filter !== "any" && { keyword: searchQuery }),
    }),
    [
      showOnlyConfirmed,
      entPointsFrom,
      entPointsTo,
      aetPointsFrom,
      aetPointsTo,
      fullness,
      hasEnglishCertificate,
      currentPage,
      searchQuery,
      filter,
    ]
  );

  const handlePostUsersFromCurrentPageClick = () => {
    services.secretary
      .postUsersFromCurrentPage(data)
      .then((response) => {
        fileDownload(response.data, "otchet.xlsx");
        console.log(response);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const handlePostUsersThatMatchQueryClick = () => {
    services.secretary
      .postUsersThatMatchQuery(data)
      .then((response) => {
        fileDownload(response.data, "otchet.xlsx");
        console.log(response);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  return (
    <Paper className={classes.paper}>
      <Toolbar>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography
              className={classes.title}
              variant="h6"
              id="tableTitle"
              component="div"
            >
              {showOnlyConfirmed
                ? "Поиск Подтвержденных Абитуриентов"
                : "Поиск Абитуриентов"}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Grid container spacing={3}>
              <Grid item lg={6} xs={12}>
                <TextField
                  placeholder="Введите ваш запрос"
                  onChange={(event) => handleSearchQueryChange(event)}
                  style={{ width: "70%" }}
                />

                <TextField
                  select
                  value={filter}
                  onChange={(event) => handleFilterChange(event)}
                  style={{ width: "30%" }}
                >
                  <MenuItem value={"FIO"} selected>
                    Полное имя
                  </MenuItem>
                  <MenuItem value={"iin"}>ИИН</MenuItem>
                  <MenuItem value={"locality"}>Город</MenuItem>
                </TextField>
              </Grid>
              <Grid item lg={3} xs={6}>
                <FormControlLabel
                  style={{ float: "right", textAlign: "center" }}
                  control={
                    <Checkbox
                      checked={hasEnglishCertificate}
                      onChange={() =>
                        setHasEnglishCertificate((prevState) => !prevState)
                      }
                      color="primary"
                    />
                  }
                  label="Имеется сертификат"
                />
              </Grid>
              <Grid item lg={3} xs={6}>
                <FormControlLabel
                  style={{ float: "right", textAlign: "center" }}
                  control={
                    <Checkbox
                      checked={fullness}
                      onChange={() => setFullness((prevState) => !prevState)}
                      color="primary"
                    />
                  }
                  label="Все данные заполнены"
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container justify="space-around" spacing={6}>
              <Grid item xs>
                <Button
                  style={{ width: "100%" }}
                  variant="contained"
                  color="primary"
                  onClick={handlePostUsersFromCurrentPageClick}
                  disabled={loading}
                >
                  Отгрузить в Excel пользователей с этой страницы (стр.{" "}
                  {currentPage + 1})
                </Button>
              </Grid>
              <Grid item xs>
                <Button
                  style={{ width: "100%" }}
                  variant="contained"
                  color="primary"
                  onClick={handlePostUsersThatMatchQueryClick}
                  disabled={loading}
                >
                  Отгрузить в Excel всех пользователей из запроса
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={2}>
            <Typography variant="subtitle1">Баллы ЕНТ</Typography>
          </Grid>
          <Grid item xs={5}>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <Typography id="input-slider-1" gutterBottom>
                  От
                </Typography>
              </Grid>
              <Grid item xs>
                <Slider
                  value={typeof entPointsFrom === "number" ? entPointsFrom : 0}
                  onChange={handleEntPointsFromSliderChange}
                  aria-labelledby="input-slider-1"
                  min={0}
                  max={entMaxPoints}
                />
              </Grid>
              <Grid item>
                <Input
                  className={classes.input}
                  value={entPointsFrom}
                  margin="dense"
                  onChange={handleEntPointsFromInputChange}
                  onBlur={handleEntPointsFromBlur}
                  inputProps={{
                    step: 10,
                    min: 0,
                    max: entMaxPoints,
                    type: "number",
                    "aria-labelledby": "input-slider-1",
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={5}>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <Typography id="input-slider-2" gutterBottom>
                  До
                </Typography>
              </Grid>
              <Grid item xs>
                <Slider
                  value={typeof entPointsTo === "number" ? entPointsTo : 0}
                  onChange={handleEntPointsToSliderChange}
                  aria-labelledby="input-slider-2"
                  min={0}
                  max={entMaxPoints}
                />
              </Grid>
              <Grid item>
                <Input
                  className={classes.input}
                  value={entPointsTo}
                  margin="dense"
                  onChange={handleEntPointsToInputChange}
                  onBlur={handleEntPointsToBlur}
                  inputProps={{
                    step: 10,
                    min: 0,
                    max: entMaxPoints,
                    type: "number",
                    "aria-labelledby": "input-slider-2",
                  }}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={2}>
            <Typography variant="subtitle1">Баллы AET</Typography>
          </Grid>
          <Grid item xs={5}>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <Typography id="input-slider-3" gutterBottom>
                  От
                </Typography>
              </Grid>
              <Grid item xs>
                <Slider
                  value={typeof aetPointsFrom === "number" ? aetPointsFrom : 0}
                  onChange={handleAetPointsFromSliderChange}
                  aria-labelledby="input-slider-3"
                  min={0}
                  max={aetMaxPoints}
                />
              </Grid>
              <Grid item>
                <Input
                  className={classes.input}
                  value={aetPointsFrom}
                  margin="dense"
                  onChange={handleAetPointsFromInputChange}
                  onBlur={handleAetPointsFromBlur}
                  inputProps={{
                    step: 10,
                    min: 0,
                    max: aetMaxPoints,
                    type: "number",
                    "aria-labelledby": "input-slider-3",
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={5}>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <Typography id="input-slider-4" gutterBottom>
                  До
                </Typography>
              </Grid>
              <Grid item xs>
                <Slider
                  value={typeof aetPointsTo === "number" ? aetPointsTo : 0}
                  onChange={handleAetPointsToSliderChange}
                  aria-labelledby="input-slider-4"
                  min={0}
                  max={aetMaxPoints}
                />
              </Grid>
              <Grid item>
                <Input
                  className={classes.input}
                  value={aetPointsTo}
                  margin="dense"
                  onChange={handleAetPointsToInputChange}
                  onBlur={handleAetPointsToBlur}
                  inputProps={{
                    step: 10,
                    min: 0,
                    max: aetMaxPoints,
                    type: "number",
                    "aria-labelledby": "input-slider-4",
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
          {showOnlyConfirmed && (
            <>
              <Button
                style={{ width: "50%" }}
                variant="contained"
                color="primary"
                onClick={() => setOpenDialog(true)}
                disabled={loading}
              >
                Перевести в ДУ
              </Button>
              <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
                <DialogTitle>
                  Вы точно хотите конвертировать абитурентов?
                </DialogTitle>
                <DialogActions>
                  <Button onClick={handleStudentConverter}>Да</Button>
                  <Button onClick={() => setOpenDialog(false)}>Нет</Button>
                </DialogActions>
              </Dialog>
            </>
          )}
        </Grid>
      </Toolbar>

      <Table aria-label="custom pagination table">
        <TableHead>
          <TableRow>
            <TableCell>Фамилия</TableCell>
            <TableCell>Имя</TableCell>
            <TableCell>Отчество</TableCell>
            <TableCell align="right">Электронная почта</TableCell>
            <TableCell align="right">Номер телефона</TableCell>
            <TableCell align="right"></TableCell>
          </TableRow>
        </TableHead>

        {loading ? (
          <TableBody>
            {[...Array(10)].map((x, i) => {
              return (
                <TableRow key={i}>
                  <TableCell component="th" scope="row">
                    <Skeleton variant="text" />
                  </TableCell>
                  <TableCell component="th" scope="row">
                    <Skeleton variant="text" />
                  </TableCell>
                  <TableCell component="th" scope="row">
                    <Skeleton variant="text" />
                  </TableCell>
                  <TableCell style={{ width: 160 }} align="right">
                    <Skeleton variant="text" />
                  </TableCell>
                  <TableCell style={{ width: 160 }} align="right">
                    <Skeleton variant="text" />
                  </TableCell>
                  <TableCell align="right">
                    <Skeleton variant="text" />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        ) : (
          <TableBody>
            {enrollees.map(
              ({ id, surname, name, patronymic, email, phoneNumber }) => (
                <TableRow key={id}>
                  <TableCell component="th" scope="row">
                    {surname}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {name}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {patronymic}
                  </TableCell>
                  <TableCell style={{ width: 160 }} align="right">
                    {email}
                  </TableCell>
                  <TableCell style={{ width: 160 }} align="right">
                    {phoneNumber}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      variant="contained"
                      color="primary"
                      target="_blank"
                      href={`/enrollees/${id}`}
                    >
                      Подробнее
                    </Button>
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      variant="contained"
                      onClick={() => handleSendToEpvo(id)}
                    >
                      Отправить в епво
                    </Button>
                  </TableCell>
                </TableRow>
              )
            )}
          </TableBody>
        )}
      </Table>
      <TablePagination
        count={enrolleesCount}
        rowsPerPage={rowsPerPage}
        page={currentPage}
        component="div"
        rowsPerPageOptions={[]}
        onChangePage={handleChangePage}
        ActionsComponent={TablePaginationActions}
      />
    </Paper>
  );
}

EnrolleeList.defaultProps = {
  showOnlyConfirmed: false,
};

export default EnrolleeList;
